import { Box, Stack, TextareaAutosize } from "@mui/material";
import QRcodeDissociation from "assets/images/tca/0/QRcodeDissociation.png";
import QRcodeGoalSetting from "assets/images/tca/0/QRcodeGoalSetting.png";
import ManagedStringList from "components/ManagedStringList";
import { DATE_FORMAT } from "constants/index";
import { TPGPLongTermGoal } from "models/tca";
import moment from "moment";
import { nanoid } from "nanoid";
import { useEffect, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import ReactToPrint from "react-to-print";

import VideoBlock from "../VideoBlock";
import YmcaPageHeader from "../YmcaPageHeader";

const StageInitial = ({ sendContractToBackend }) => {
  const [longTermGoals, setLongTermGoals] = useState([]);
  const formContext = useFormContext();
  const { control, setValue, watch } = formContext;
  const printTemplateRef = useRef();
  const watchFieldsToPrint = watch(["smsInitial.longTermGoals", "smsInitial.dissociationNote"]);

  //#region event handlers
  const handleUpdateLongTermGoals = (values: string[]) => {
    setValue("smsInitial.longTermGoals", values);
    setLongTermGoals(values);
    //Prepare PGP
    let goals: TPGPLongTermGoal[] = [];
    values.forEach((e) => {
      goals.push({
        id: nanoid(),
        longTermGoal: e,
        ratingFarFromGoal: 1,
        timestamp: moment().format(DATE_FORMAT),
      });
    });
    setValue("pgp", goals);
  };
  //#endregion event handlers

  return (
    <>
      <Box className="greenContainer">
        <Box className="containerPadding">
          <Stack>
            <Box className="header1" mb={"20px"}>
              Initial Appointment
            </Box>
            <Box className="header2 green" mb={"16px"}>
              GOAL SETTING
            </Box>
          </Stack>
          <VideoBlock
            thumbnail="https://img.youtube.com/vi/wGDt1-C6-5c/maxresdefault.jpg"
            youtubeLink={
              "https://www.youtube.com/watch?v=wGDt1-C6-5c&list=PLgBxj8BHl0M33FtRIckAJCR0JEjqz2ujJ&index=5"
            }
          >
            <>
              <Box className="text text-bold video-title" mt={0.75} mb={2}>
                Watch this video about goal setting to get started!
              </Box>
              <Box className="text text-bold">
                Goal Setting is extremely important in being able to stay on track and create
                attainable action steps. Remember to set <span className="orange">S.M.A.R.T.</span>{" "}
                goals!
              </Box>
            </>
          </VideoBlock>
          <Stack className="whiteBox" mb={"20px"}>
            <Box className="header2 green" mb={"11px"}>
              S.M.A.R.T. principle:
            </Box>
            <Stack direction={"row"} gap={3.5}>
              <Stack>
                <Box className="text text-bold orange" mb={2}>
                  S=Specific
                </Box>
                <Box className="text">
                  Specific Goals that are "do your best goals" are not specific and don't lend
                  themselves to being able to be built upon.
                </Box>
              </Stack>
              <Stack>
                <Box className="text text-bold orange" mb={2}>
                  M=Measurable
                </Box>
                <Box className="text">
                  Measurable In order for something to be measurable we need to have some metrics or
                  values that we're measuring, like walking for 20 minutes, 3 times per week.
                </Box>
              </Stack>
              <Stack>
                <Box className="text text-bold orange" mb={2}>
                  A=Attainable
                </Box>
                <Box className="text">
                  Be sure that your goals are truly doable given your life's variables. Walking for
                  60 minutes 7 days a week is very aggressive and difficult for even the most
                  seasoned exerciser to attain.
                </Box>
              </Stack>
              <Stack>
                <Box className="text text-bold orange" mb={2}>
                  R=Recorded
                </Box>
                <Box className="text">
                  Recorded Be sure that you have a way to record your activity so that trends can be
                  reviewed. An App or paper and pen work great.
                </Box>
              </Stack>
              <Stack>
                <Box className="text text-bold orange" mb={2}>
                  T=Time Based
                </Box>
                <Box className="text">
                  Time Based Be sure that you have a date set when you can assess your goal progress
                  and set new goals.
                </Box>
              </Stack>
            </Stack>
          </Stack>
          <Box className="text text-bold" mb={2}>
            Write your Long-term goal(s) below as a reminder of the path that you are taking and
            WILL accomplish.
          </Box>
          <ManagedStringList
            limit={3}
            list={watchFieldsToPrint[0]}
            callback={handleUpdateLongTermGoals}
            length={50}
          />
        </Box>
        <hr className="containerDivider" />
        <Stack className="containerPadding">
          <Box className="header2 green" mb={"16px"}>
            DISSOCIATION
          </Box>
          <VideoBlock
            thumbnail="https://img.youtube.com/vi/PXJljPtSrB0/maxresdefault.jpg"
            youtubeLink={
              "https://www.youtube.com/watch?v=PXJljPtSrB0&list=PLgBxj8BHl0M33FtRIckAJCR0JEjqz2ujJ&index=3"
            }
          >
            <Box className="text text-bold video-title" mt={0.75} mb={2}>
              Watch this video about dissociation to get started!
            </Box>
          </VideoBlock>
          <Box className="text text-bold">
            Dissociation is something that you do without even knowing it! The definition of
            dissociation is the disconnection or separation of something from something else or the
            state of being disconnected.
          </Box>
          <Box className="text text-bold" mt={2}>
            What does this mean from the perspective of exercise adherence? It means that when we
            exercise, we may need to find ways that we can take our mind off the discomfort or
            boredom of our exercise plan.
          </Box>
          <Box className="text text-bold" mt={2}>
            Take a few minutes and think about what these might be for you. A few examples might
            include listening to music or podcasts, working out with a friend and/or watching TV
            while exercising.
          </Box>
          <Box className="text text-bold" mt={2}>
            Use the space below and write a few of your own ideas that you can leverage.
          </Box>
          <Controller
            name="smsInitial.dissociationNote"
            control={control}
            rules={{ required: false }}
            render={({ field }) => (
              <Stack spacing={1} mt={2} className="note-input-container large">
                <TextareaAutosize
                  {...field}
                  aria-label="description textarea"
                  className="note-textarea"
                  placeholder="Please enter"
                />
              </Stack>
            )}
          />
        </Stack>
        <hr className="containerDivider" />
        <Stack direction={"row"} justifyContent="space-around">
          <button type="button" className="action-button" onClick={() => sendContractToBackend()}>
            Email Document to the Member
          </button>
          <ReactToPrint
            trigger={() => (
              <button type="button" className="action-button">
                Print Document
              </button>
            )}
            content={() => printTemplateRef.current}
          />
        </Stack>
      </Box>
      <div className="out-of-sight">
        <Stack ref={printTemplateRef} className="elementToPrint">
          <YmcaPageHeader />
          <Stack direction={"row"} gap={2}>
            <img src={QRcodeGoalSetting} width="114" height="114" />
            <Stack>
              <h1 style={{ color: "#00a98f" }}>GOAL SETTING</h1>
              <Box mb={2}>
                <p>Watch this video about goal setting to get started!</p>
              </Box>
              <Box>
                <p>
                  Goal Setting is extremely important in being able to stay on track and create
                  attainable action steps. Remember to set{" "}
                  <span className="orange">S.M.A.R.T.</span> goals!
                </p>
              </Box>
            </Stack>
          </Stack>
          <Stack>
            <Box mt={4} mb={2}>
              <h2>SMART principle</h2>
            </Box>
            <h2 className="orange">S=Specific</h2>
            <Box mb={2}>
              <p>
                Specific Goals that are "do your best goals" are not specific and don't lend
                themselves to being able to be built upon.
              </p>
            </Box>
            <h2 className="orange">M=Measurable</h2>
            <Box mb={2}>
              <p>
                Measurable In order for something to be measurable we need to have some metrics or
                values that we're measuring, like walking for 20 minutes, 3 times per week.
              </p>
            </Box>
            <h2 className="orange">A=Attainable</h2>
            <Box mb={2}>
              <p>
                Be sure that your goals are truly doable given your life's variables. Walking for 60
                minutes 7 days a week is very aggressive and difficult for even the most seasoned
                exerciser to attain.
              </p>
            </Box>
            <h2 className="orange">R=Recorded</h2>
            <Box mb={2}>
              <p>
                Recorded Be sure that you have a way to record your activity so that trends can be
                reviewed. An App or paper and pen work great.
              </p>
            </Box>
            <h2 className="orange">T=Time Based</h2>
            <Box mb={2}>
              <p>
                Time Based Be sure that you have a date set when you can assess your goal progress
                and set new goals.
              </p>
            </Box>
            <Box mt={2} mb={2}>
              <p>
                Write your Long-term goal(s) below as a reminder of the path that you are taking and
                WILL accomplish.
              </p>
            </Box>
            <Box
              className="filledFormField"
              style={{
                background: "#edf6ff",
                border: "border: dotted 1px #313a3d",
                padding: "10px",
              }}
            >
              {longTermGoals && (
                <ul>
                  {longTermGoals.map((m, index) => (
                    <li key={`ltgoal_${index}`}>{m}</li>
                  ))}
                </ul>
              )}
            </Box>
          </Stack>
          <div
            className="page-break"
            style={{ display: "block", marginTop: "16px", pageBreakBefore: "always" }}
          />
          <br />
          <br />
          <br />
          <Stack>
            <YmcaPageHeader />
            <Stack direction={"row"} gap={2}>
              <img src={QRcodeDissociation} width="114" height="114" />
              <Stack>
                <h1 style={{ color: "#00a98f" }}>DISSOCIATION</h1>
                <Box mb={2}>
                  <p>Watch this video about dissociation to get started!</p>
                </Box>
              </Stack>
            </Stack>
            <Box mt={2}>
              <p>
                Dissociation is something that you do without even knowing it! The definition of
                dissociation is the disconnection or separation of something from something else or
                the state of being disconnected.
              </p>
            </Box>
            <Box mt={2}>
              <p>
                What does this mean from the perspective of exercise adherence? It means that when
                we exercise, we may need to find ways that we can take our mind off the discomfort
                or boredom of our exercise plan.
              </p>
            </Box>
            <Box mt={2}>
              <p>
                Take a few minutes and think about what these might be for you. A few examples might
                include listening to music or podcasts, working out with a friend and/or watching TV
                while exercising.
              </p>
            </Box>
            <Box mt={2}>
              <p>Use the space below and write a few of your own ideas that you can leverage.</p>
            </Box>
            <Box
              className="filledFormField p_wrap"
              mt={2}
              style={{
                background: "#edf6ff",
                border: "dotted 1px #313a3d",
                padding: "10px",
                whiteSpace: "pre-line",
              }}
            >
              {watchFieldsToPrint[1]}
            </Box>
          </Stack>
        </Stack>
      </div>
    </>
  );
};

export default StageInitial;
